import { useCallback, useId, useRef, type ReactNode } from 'react';

import { Button } from './Button';
import { useFocusTrap } from './useFocusTrap';

export interface DrawerProps {
  /** Whether the drawer is on screen. */
  readonly open: boolean;
  /** Called on Escape and on the close control. */
  readonly onClose: () => void;
  /** The drawer's accessible name; rendered as its heading. */
  readonly title: string;
  /** Optional controls rendered in the header beside the title. */
  readonly headerContent?: ReactNode;
  /** Which edge it slides from. */
  readonly side?: 'end' | 'start';
  /**
   * Modal drawers trap focus and dim the page; inline drawers sit beside the
   * content and leave it usable. The assistant is inline — reading a record
   * while the assistant is open is the entire point of it.
   */
  readonly modal?: boolean;
  /** Controls pinned to the bottom of the panel. */
  readonly footer?: ReactNode;
  /** The body. */
  readonly children: ReactNode;
}

/**
 * A side panel.
 *
 * The `modal` distinction is the reason this is not just a Modal with different
 * CSS. A modal drawer is a dialog: it takes focus, dims the page and must be
 * dismissed. An inline drawer is a region: it shares the screen with the work
 * it comments on, keeps the page interactive, and is announced as a
 * complementary landmark rather than a dialog — which is exactly how an
 * assistant that only ever *assists* should present itself.
 *
 * @param props see {@link DrawerProps}
 * @returns the drawer, or nothing when closed
 */
export function Drawer({
  open,
  onClose,
  title,
  headerContent,
  side = 'end',
  modal = false,
  footer,
  children,
}: DrawerProps): React.JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = `${useId()}-title`;

  const handleEscape = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleTrapKeyDown = useFocusTrap(panelRef, open && modal, handleEscape);

  if (!open) {
    return null;
  }

  const panel = (
    <div
      ref={panelRef}
      className="fo-drawer"
      data-side={side}
      data-modal={modal ? '' : undefined}
      role={modal ? 'dialog' : 'complementary'}
      aria-modal={modal ? true : undefined}
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={
        modal
          ? handleTrapKeyDown
          : (event) => {
              // An inline drawer has no focus trap, so Escape is handled here.
              if (event.key === 'Escape') {
                onClose();
              }
            }
      }
    >
      {/* Plain elements, not <header>/<footer>. Those map to the banner and
          contentinfo landmarks unless they sit inside sectioning content, and a
          panel is not the page — a drawer that announced itself as the site
          banner would put two banners in the landmark list. */}
      <div className="fo-drawer__header">
        <h2 className="fo-drawer__title" id={titleId}>
          {title}
        </h2>
        {headerContent}
        <Button variant="ghost" size="sm" icon="close" iconOnly onClick={onClose}>
          Close
        </Button>
      </div>
      <div className="fo-drawer__body">{children}</div>
      {footer === undefined ? null : <div className="fo-drawer__footer">{footer}</div>}
    </div>
  );

  if (!modal) {
    return panel;
  }

  return (
    <div className="fo-overlay" data-variant="drawer">
      <div
        className="fo-overlay__scrim"
        aria-hidden="true"
        onClick={() => {
          onClose();
        }}
      />
      {panel}
    </div>
  );
}
